import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Building2, TrendingUp } from 'lucide-react';
import { api, Department } from '../../lib/api';
import { exportToCsv, CsvColumn } from '../../lib/csv';

const trialBalanceCsvColumns: CsvColumn[] = [
  { key: 'accountCode', label: '科目コード' },
  { key: 'accountName', label: '勘定科目' },
  { key: 'debitBalance', label: '借方残高' },
  { key: 'creditBalance', label: '貸方残高' },
  { key: 'budgetAmount', label: '予算額' },
  { key: 'variance', label: '差異' },
];

export function TrialBalancePage() {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState<'standard' | 'budget' | 'department'>('standard');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['trial-balance', date, selectedDepartment],
    queryFn: () => {
      const params: Record<string, string> = { date };
      if (selectedDepartment) {
        params.departmentId = selectedDepartment;
      }
      return api.get<{ data: any[]; totals: any }>('/api/journals/trial-balance', params);
    },
  });

  const { data: departments } = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<{ data: Department[] }>('/api/departments', { flat: 'true' }),
  });

  const groupedData = data?.data.reduce((acc: any, row: any) => {
    const type = row.accountType;
    if (!acc[type]) acc[type] = [];
    acc[type].push(row);
    return acc;
  }, {});

  const typeLabels: Record<string, string> = {
    asset: '資産',
    liability: '負債',
    equity: '純資産',
    revenue: '収益',
    expense: '費用',
  };

  const handleExportCsv = () => {
    if (data?.data) {
      const exportData = data.data.map((row: any) => ({
        ...row,
        budgetAmount: row.budgetAmount || 0,
        variance: (row.debitBalance || row.creditBalance || 0) - (row.budgetAmount || 0),
      }));
      exportToCsv(exportData, trialBalanceCsvColumns, '試算表');
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          試算表
        </h1>
        <button
          onClick={handleExportCsv}
          disabled={!data?.data?.length}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm disabled:opacity-50"
          style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
        >
          <Download size={16} />
          CSV出力
        </button>
      </div>

      {/* フィルター & 表示モード */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
              基準日
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="px-3 py-2 rounded-lg border text-sm"
              style={{
                backgroundColor: 'var(--input-bg)',
                borderColor: 'var(--input-border)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
              表示モード
            </label>
            <div className="flex items-center gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
              <button
                onClick={() => setViewMode('standard')}
                className={`px-3 py-1.5 rounded text-sm transition-colors ${
                  viewMode === 'standard' ? 'bg-white dark:bg-gray-700 shadow' : ''
                }`}
                style={{ color: 'var(--text-primary)' }}
              >
                標準
              </button>
              <button
                onClick={() => setViewMode('budget')}
                className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
                  viewMode === 'budget' ? 'bg-white dark:bg-gray-700 shadow' : ''
                }`}
                style={{ color: 'var(--text-primary)' }}
              >
                <TrendingUp size={14} />
                予算対比
              </button>
              <button
                onClick={() => setViewMode('department')}
                className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
                  viewMode === 'department' ? 'bg-white dark:bg-gray-700 shadow' : ''
                }`}
                style={{ color: 'var(--text-primary)' }}
              >
                <Building2 size={14} />
                部門別
              </button>
            </div>
          </div>

          {viewMode === 'department' && (
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                部門
              </label>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="px-3 py-2 rounded-lg border text-sm min-w-[200px]"
                style={{
                  backgroundColor: 'var(--input-bg)',
                  borderColor: 'var(--input-border)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="">全部門</option>
                {departments?.data.map((dept) => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ color: 'var(--text-primary)' }}>科目コード</th>
                <th style={{ color: 'var(--text-primary)' }}>勘定科目</th>
                <th className="text-right" style={{ color: 'var(--text-primary)' }}>借方残高</th>
                <th className="text-right" style={{ color: 'var(--text-primary)' }}>貸方残高</th>
                {viewMode === 'budget' && (
                  <>
                    <th className="text-right" style={{ color: 'var(--text-primary)' }}>予算額</th>
                    <th className="text-right" style={{ color: 'var(--text-primary)' }}>差異</th>
                    <th className="text-right" style={{ color: 'var(--text-primary)' }}>達成率</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {groupedData && Object.entries(groupedData).map(([type, items]: [string, any]) => (
                <>
                  <tr key={type} style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                    <td colSpan={viewMode === 'budget' ? 7 : 4} className="font-bold" style={{ color: 'var(--text-primary)' }}>
                      【{typeLabels[type] || type}】
                    </td>
                  </tr>
                  {items.map((row: any) => {
                    const balance = row.debitBalance || row.creditBalance || 0;
                    const budget = row.budgetAmount || 0;
                    const variance = balance - budget;
                    const achievementRate = budget > 0 ? ((balance / budget) * 100).toFixed(1) : '-';
                    
                    return (
                      <tr key={row.accountId}>
                        <td className="font-mono" style={{ color: 'var(--text-secondary)' }}>{row.accountCode}</td>
                        <td style={{ color: 'var(--text-primary)' }}>{row.accountName}</td>
                        <td className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                          {row.debitBalance > 0 ? `¥${row.debitBalance.toLocaleString()}` : '-'}
                        </td>
                        <td className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                          {row.creditBalance > 0 ? `¥${row.creditBalance.toLocaleString()}` : '-'}
                        </td>
                        {viewMode === 'budget' && (
                          <>
                            <td className="text-right font-mono" style={{ color: 'var(--text-secondary)' }}>
                              {budget > 0 ? `¥${budget.toLocaleString()}` : '-'}
                            </td>
                            <td 
                              className="text-right font-mono" 
                              style={{ color: variance >= 0 ? 'var(--success)' : 'var(--error)' }}
                            >
                              {budget > 0 ? (variance >= 0 ? '+' : '') + `¥${variance.toLocaleString()}` : '-'}
                            </td>
                            <td 
                              className="text-right font-mono" 
                              style={{ color: typeof achievementRate === 'string' ? 'var(--text-secondary)' : (parseFloat(achievementRate as any) >= 100 ? 'var(--success)' : 'var(--warning)') }}
                            >
                              {achievementRate !== '-' ? `${achievementRate}%` : '-'}
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </>
              ))}
              {data?.totals && (
                <tr style={{ backgroundColor: 'var(--light-gray-secondary)' }}>
                  <td colSpan={2} className="text-right font-bold" style={{ color: 'var(--text-primary)' }}>
                    合計
                  </td>
                  <td className="text-right font-mono font-bold" style={{ color: 'var(--text-primary)' }}>
                    ¥{(data.totals.debitBalance || 0).toLocaleString()}
                  </td>
                  <td className="text-right font-mono font-bold" style={{ color: 'var(--text-primary)' }}>
                    ¥{(data.totals.creditBalance || 0).toLocaleString()}
                  </td>
                  {viewMode === 'budget' && (
                    <>
                      <td colSpan={3}></td>
                    </>
                  )}
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
